/**
 * Types for flexible rebate analytics dimension configuration
 */

import {
  CalculationTypeDisplayNames,
  UnifiedForecastResult,
} from '@erp/shared-types';

export interface MultiYearForecastData {
  currentYear: number;
  currentYearData: UnifiedForecastResult | null;
  previousYearData: UnifiedForecastResult | null;
  isCumulative?: boolean; // Whether data is cumulative (affects breakdown table display)
}

export enum DimensionType {
  TIME = 'time',
  SUPPLIER = 'supplier',
  DIVISION = 'division',
  RULE = 'rule',
  AGREEMENT = 'agreement',
  RULE_TAG = 'rule_tag',
  AGREEMENT_TAG = 'agreement_tag',
  SUPPLIER_TAG = 'supplier_tag',
  RULE_TYPE = 'rule_type',
}

export const DIMENSION_LABELS: Record<DimensionType, string> = {
  [DimensionType.TIME]: 'Month',
  [DimensionType.SUPPLIER]: 'Supplier',
  [DimensionType.DIVISION]: 'Division',
  [DimensionType.RULE]: 'Rule',
  [DimensionType.AGREEMENT]: 'Agreement',
  [DimensionType.RULE_TAG]: 'Rule Tag',
  [DimensionType.AGREEMENT_TAG]: 'Agreement Tag',
  [DimensionType.SUPPLIER_TAG]: 'Supplier Tag',
  [DimensionType.RULE_TYPE]: 'Rule Type',
};

/**
 * Metric displayed in table cells.
 * - 'rebateAmount': rebate dollars (supports cumulative view via backend
 *   cumulativeRebateAmount)
 * - 'affectedVolume': purchase volume the rebate is paid on, per rule
 *   (manual rules have no volume and show 0; can double-count when multiple
 *   rules affect the same purchases). Supports cumulative via running sum
 *   along the TIME axis.
 * - 'totalVolume': total purchase volume from invoices, by supplier/division/
 *   agreement/month (future months are run-rate projected). Supports
 *   cumulative via running sum along the TIME axis.
 */
export type TableMetric = 'rebateAmount' | 'affectedVolume' | 'totalVolume';

export const DEFAULT_TABLE_METRIC: TableMetric = 'rebateAmount';

export const TABLE_METRIC_LABELS: Record<TableMetric, string> = {
  rebateAmount: 'Rebate Amount',
  affectedVolume: 'Affected Volume',
  totalVolume: 'Total Volume',
};

/** Volume metrics (as opposed to rebate dollars). */
export function isVolumeTableMetric(metric: TableMetric | undefined): boolean {
  return metric === 'affectedVolume' || metric === 'totalVolume';
}

export type TimeFilterPreset =
  | 'ytd'
  | 'all'
  | 'prev_and_current'
  | 'current'
  | 'previous'
  | 'current_and_next'
  | 'yoy';

export type LineType =
  | 'estimates'
  | 'linear-regression'
  | 'conservative'
  | 'reported'
  | 'previousYear';

export const LINE_TYPE_LABELS: Record<string, string> = {
  estimates: 'Estimates',
  'linear-regression': CalculationTypeDisplayNames['linear-regression'],
  conservative: CalculationTypeDisplayNames['conservative'],
  reported: 'Reported',
  previousYear: 'Previous Year',
  // Legacy alias for report pages that still use 'predictions' as a data source type
  predictions: 'Predictions',
};

export const LINE_TYPE_DESCRIPTIONS: Record<string, string> = {
  estimates: 'Historical actuals and YTD estimates',
  'linear-regression': 'Linear regression forecast',
  conservative: `${CalculationTypeDisplayNames['conservative']} forecast`,
  reported: 'Frozen predictions',
  previousYear: 'Prior year comparison (always Estimates)',
  predictions: 'Machine learning forecasts',
};

/** Known prediction strategy keys */
const KNOWN_PREDICTION_STRATEGIES = new Set([
  'linear-regression',
  'conservative',
]);

/** Check if a line type string is a prediction strategy */
export function isPredictionStrategy(lineType: string): boolean {
  return KNOWN_PREDICTION_STRATEGIES.has(lineType);
}

/** Color config for prediction strategies (past = before split, future = after split) */
export const PREDICTION_STRATEGY_COLORS: Record<
  string,
  { past: string; future: string }
> = {
  'linear-regression': { past: '#9C27B0', future: '#1976D2' }, // magenta / blue (colors.info.main)
  conservative: { past: '#B45309', future: '#F59E0B' }, // dark amber / amber
};

/** Get prediction strategy colors with gray fallback for unknown strategies */
export function getPredictionStrategyColors(strategy: string): {
  past: string;
  future: string;
} {
  return (
    PREDICTION_STRATEGY_COLORS[strategy] || {
      past: '#6B7280',
      future: '#9CA3AF',
    }
  );
}

export enum FormulaOperation {
  ADD = 'add',
  SUBTRACT = 'subtract',
  MULTIPLY = 'multiply',
  DIVIDE = 'divide',
}

export interface CustomColumn {
  id: string;
  name: string;
  leftOperand: string;
  operation: FormulaOperation;
  rightOperand: string | number;
}

export interface DimensionGroup {
  id: string;
  name: string;
  itemIds: string[]; // Items (tags, suppliers, divisions, rules, agreements) that belong to this group
}

export interface DimensionGroupingConfig {
  userGroups: DimensionGroup[]; // Editable, deletable user-created groups
  catchAllGroups: DimensionGroup[]; // Immutable structure, editable names
  // For RULE_TAG: catchAllGroups.length === 2 (Conditional and Unconditional)
  // For all other dimensions: catchAllGroups.length === 1
}

// Default catch-all group names for each dimension type
// RULE_TYPE is excluded because it doesn't support grouping (only 2 values)
export const DEFAULT_CATCH_ALL_NAMES: Record<
  Exclude<DimensionType, DimensionType.TIME | DimensionType.RULE_TYPE>,
  string
> = {
  [DimensionType.SUPPLIER]: 'Other',
  [DimensionType.DIVISION]: 'Other',
  [DimensionType.RULE]: 'Other',
  [DimensionType.AGREEMENT]: 'Other',
  [DimensionType.RULE_TAG]: 'All other Conditional', // For first group, second is "All other Unconditional"
  [DimensionType.AGREEMENT_TAG]: 'Standard',
  [DimensionType.SUPPLIER_TAG]: 'Standard',
};

// Special IDs for RULE_TAG catch-all groups
export const RULE_TAG_CONDITIONAL_CATCH_ALL_ID = '__catch_all_conditional__';
export const RULE_TAG_UNCONDITIONAL_CATCH_ALL_ID =
  '__catch_all_unconditional__';

/**
 * Create default catch-all groups for a dimension type
 * RULE_TYPE is excluded because it doesn't support grouping (only 2 values)
 */
export function createDefaultCatchAllGroups(
  dimensionType: Exclude<
    DimensionType,
    DimensionType.TIME | DimensionType.RULE_TYPE
  >,
): DimensionGroup[] {
  if (dimensionType === DimensionType.RULE_TAG) {
    return [
      {
        id: RULE_TAG_CONDITIONAL_CATCH_ALL_ID,
        name: 'All other Conditional',
        itemIds: [], // Catch-all groups don't have specific item IDs
      },
      {
        id: RULE_TAG_UNCONDITIONAL_CATCH_ALL_ID,
        name: 'All other Unconditional',
        itemIds: [],
      },
    ];
  }

  return [
    {
      id: `__catch_all_${dimensionType}__`,
      name: DEFAULT_CATCH_ALL_NAMES[dimensionType],
      itemIds: [],
    },
  ];
}

export interface TableConfig {
  id: string;
  // 4-dimension structure
  primaryXDimension: DimensionType; // Always TIME
  secondaryXDimension?: DimensionType; // Optional nested columns
  primaryYDimension: DimensionType; // Main row groups
  secondaryYDimension?: DimensionType; // Optional breakdown within groups
  selectedMonths?: string[]; // Filter months when Time is primary X (legacy, use timeFilterPreset instead)
  timeFilterPreset?: TimeFilterPreset; // Time filter preset (YTD, All, etc.)
  selectedRebatePointTypes?: string[]; // e.g., ['estimates', 'linear-regression']
  metric?: TableMetric; // Cell value metric (default: 'rebateAmount')
  customColumns?: CustomColumn[]; // array of custom calculated columns
  dimensionGroupingConfig?: Partial<
    Record<
      Exclude<DimensionType, DimensionType.TIME | DimensionType.RULE_TYPE>,
      DimensionGroupingConfig
    >
  >; // Grouping config for all dimensions except TIME and RULE_TYPE (RULE_TYPE doesn't support grouping)
  label: string; // User-defined or auto-generated display name
  creatorName?: string; // Name of user who created the table
  creatorId?: string; // ID of user who created the table (for persistence)
}

/**
 * Generate a human-readable label for a table configuration
 */
export function generateTableLabel(
  config: Omit<TableConfig, 'id' | 'label'>,
): string {
  const xDimensions = [
    DIMENSION_LABELS[config.primaryXDimension],
    config.secondaryXDimension
      ? DIMENSION_LABELS[config.secondaryXDimension]
      : null,
  ].filter(Boolean);

  const yDimensions = [
    DIMENSION_LABELS[config.primaryYDimension],
    config.secondaryYDimension
      ? DIMENSION_LABELS[config.secondaryYDimension]
      : null,
  ].filter(Boolean);

  let label = `${xDimensions.join(' × ')} × ${yDimensions.join(' × ')}`;

  // Add month filter info if months are selected
  if (
    config.selectedMonths &&
    config.selectedMonths.length > 0 &&
    config.primaryXDimension === DimensionType.TIME
  ) {
    const monthDisplay = config.selectedMonths
      .map((month) => {
        if (month === 'Current') return 'Current Month';
        if (month === 'Previous Month') return 'Previous Month';
        if (month === 'Next Month') return 'Next Month';
        return month;
      })
      .join(', ');
    label += ` (${monthDisplay})`;
  }

  return label;
}
